import Vue from 'vue';

import i18n from '@/i18n';

import { getRoutingProfiles } from '@/services/routing-profiles';

/**
 * @typedef {Object} Profile
 * @property {string} name the human-readable name of the profile
 * @property {'last_call_profile'|'course_profile'} type the type of the profile
 * @property {number} order the order in which the profile should appear
 * @property {boolean} is_active whether the profile is currently active
 * @property {string[]} lines a list of selected lines (course_profile)
 * @property {string[]} regions a list of selected regions (course_profile)
 * @property {string} target_user the numeric identifier of the user associated with this profile (course_profile)
 * @property {number} limit_minutes the number of minutes under which the last call has been made (last_call_profile)
 * @property {?string} special a text to display for special profiles (either last_call_profile, or "default profile" - a virtual profile that is not saved)
 * @property {?boolean} disableEdit a boolean used to prevent any modification of the profile (useful for the "default profile" - a virtual profile that is not saved)
 */

class Profile {
  name;
  target_user;
  lines = [];
  regions = [];
  is_active = false;
  special;
  limit_minutes;
  type;

  constructor(options) {
    Object.assign(this, options);
    if (this.target_user && !Array.isArray(this.target_user)) {
      this.target_user = [this.target_user]; //ToDo: update when API changes
    }
    if (this.type === 'last_call_profile') {
      this.name = i18n.t('exploitationcenter.profile.last-call-title');
      this.special = i18n.t('exploitationcenter.profile.last-call-text');
    }
  }
}

export const state = {
  profiles: [],
};

export const mutations = {
  setProfiles(state, profiles) {
    state.profiles = profiles.map(p => new Profile(p));
  },
  editProfile(state, { index, profile }) {
    Vue.set(state.profiles, index, new Profile(profile));
  },
  deleteProfile(state, index) {
    state.profiles.splice(index, 1);
  },
};

export const actions = {
  getProfiles({ commit, rootState }) {
    if (rootState.auth.user.can_access_routing_profiles) {
      return getRoutingProfiles().then(response => {
        commit('setProfiles', response.data);
      });
    }
  },
  addProfile({ commit, state, rootState }, name) {
    if (rootState.auth.user.can_access_routing_profiles) {
      const profile = { name, order: 0, type: 'course_profile' };
      commit('setProfiles', state.profiles.concat([profile]));
    }
  },
  editProfile({ commit, state, rootState }, opts) {
    if (rootState.auth.user.can_access_routing_profiles) {
      const { item, profile } = opts;
      const index = state.profiles.indexOf(item);
      commit('editProfile', { index, profile });
    }
  },
  deleteProfile({ commit, state, rootState }, item) {
    if (rootState.auth.user.can_access_routing_profiles) {
      const index = state.profiles.indexOf(item);
      commit('deleteProfile', index);
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
